<template>
  <div class="help-center-page">
    <h1>{{ pageContent.title }}</h1>

    <input
      type="text"
      v-model="searchQuery"
      :placeholder="pageContent.searchPlaceholder"
      class="search-input"
    >

    <div v-if="filteredQaItems.length === 0 && searchQuery" class="no-results">
      {{ pageContent.noResultsForQuery }}
    </div>

    <div v-for="categoryData in filteredQaItems" :key="categoryData.id" class="qa-category">
      <h2>{{ categoryData.title }}</h2>
      <ul>
        <li v-for="item in categoryData.items" :key="item.id" class="qa-item">
          <div class="question" @click="toggleAnswer(item)">
            {{ item.question }}
            <span class="toggle-icon">{{ item.isOpen ? '▲' : '▼' }}</span>
          </div>
          <transition name="fade">
            <div v-if="item.isOpen" class="answer">
              <p>{{ item.answer }}</p>
            </div>
          </transition>
        </li>
      </ul>
    </div>
     <div v-if="filteredQaItems.length === 0 && !searchQuery" class="no-results">
      {{ pageContent.noResultsDefault }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { helpCenterContent } from '@/content/helpCenter';

const { locale } = useI18n();
const pageContent = computed(
  () => helpCenterContent[locale.value] ?? helpCenterContent['en-US']
);

const searchQuery = ref('');

const createQaState = (categories) =>
  categories.map((category) => ({
    id: category.id,
    title: category.title,
    items: category.items.map((item) => ({
      ...item,
      isOpen: false
    }))
  }));

const qaData = ref(createQaState(pageContent.value.categories));

watch(
  pageContent,
  (newContent) => {
    qaData.value = createQaState(newContent.categories);
    searchQuery.value = '';
  }
);

const filteredQaItems = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) {
    return qaData.value;
  }

  return qaData.value
    .map((category) => {
      const matchedItems = category.items.filter(
        (item) =>
          item.question.toLowerCase().includes(query) ||
          item.answer.toLowerCase().includes(query)
      );
      if (matchedItems.length === 0) {
        return null;
      }
      return {
        ...category,
        items: matchedItems
      };
    })
    .filter(Boolean);
});

const toggleAnswer = (item) => {
  item.isOpen = !item.isOpen;
};

</script>

<style scoped>
.help-center-page {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 25px;
}

.search-input {
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 25px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1em;
}

.no-results {
  text-align: center;
  color: #777;
  margin-top: 20px;
  padding: 15px;
  background-color: #fff;
  border: 1px dashed #ddd;
  border-radius: 4px;
}

.qa-category {
  margin-bottom: 25px;
}

.qa-category h2 {
  font-size: 1.4em;
  color: #444;
  padding-bottom: 8px;
  border-bottom: 2px solid #eee;
  margin-bottom: 15px;
}

.qa-category ul {
  list-style-type: none;
  padding: 0;
}

.qa-item {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
}

.question {
  padding: 15px;
  cursor: pointer;
  font-weight: bold;
  color: #555;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
}

.question:hover {
  background-color: #f5f5f5;
}

.toggle-icon {
  font-size: 0.8em;
  color: #777;
}

.answer {
  padding: 0 15px 15px 15px; /* Adjust padding for content below question */
  color: #666;
  line-height: 1.6;
  border-top: 1px solid #eee;
  margin-top: -1px; /* Overlap with question's bottom border if any */
}

.answer p {
  margin: 0;
  padding-top: 10px;
}

/* Fade transition for answer visibility */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
  transform-origin: top;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}
</style>
